import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, Trophy, Search, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { getInitials } from "@/lib/image";
import AdminNav from "@/components/AdminNav";
import TopperEditDialog from "@/components/TopperEditDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminToppers() {
  const { user, authHeader } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [course, setCourse] = useState("all");
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {id...} = edit
  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/toppers`);
      setList(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load toppers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  const courses = useMemo(() => {
    const set = new Set(list.map((t) => t.course));
    return ["all", ...Array.from(set)];
  }, [list]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((t) =>
      (course === "all" || t.course === course) &&
      (!q || t.name.toLowerCase().includes(q))
    );
  }, [list, query, course]);

  const onCreate = () => { setEditing({}); setOpen(true); };
  const onEdit = (t) => { setEditing(t); setOpen(true); };

  const onDelete = async (t) => {
    if (!confirm(`Delete "${t.name}" permanently?`)) return;
    setDeletingId(t.id);
    try {
      await axios.delete(`${API}/toppers/${t.id}`, { headers: authHeader });
      setList((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Topper removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const onSaved = () => fetchAll();

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <Loader2 className="w-8 h-8 animate-spin text-[#002570]" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <AdminNav />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Admin Dashboard</p>
            <h1 className="mt-2 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a]">Toppers</h1>
            <p className="mt-2 font-inter text-sm text-slate-600">Manage the pass-outs that appear on your Toppers page and course pages.</p>
          </div>
          <button
            onClick={onCreate}
            data-testid="topper-add-btn"
            className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-5 py-3 text-sm font-inter font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Topper
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 mb-6 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-toppers-search"
              placeholder="Search by name…"
              className="w-full bg-[#f8fafc] rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            />
          </div>
          <select
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            data-testid="admin-toppers-course-filter"
            className="bg-[#f8fafc] rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition md:min-w-[180px]"
          >
            {courses.map((c) => <option key={c} value={c}>{c === "all" ? "All Courses" : c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm text-center py-20 px-6">
            <Trophy className="w-10 h-10 mx-auto text-[#73c3ff]" />
            <p className="mt-4 font-rubik text-lg font-semibold text-[#0f172a]">No toppers yet</p>
            <p className="mt-1 font-inter text-sm text-slate-500">Click "Add Topper" to celebrate your first pass-out.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-toppers-grid">
            {filtered.map((t) => (
              <div
                key={t.id}
                data-testid={`admin-topper-card-${t.id}`}
                className={`bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-lg transition-all ${!t.featured ? "opacity-70" : ""}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-[#002570] to-[#001a52] text-white flex items-center justify-center font-rubik text-lg font-bold shrink-0">
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} className="w-full h-full object-cover" />
                    ) : (
                      <span>{getInitials(t.name)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-rubik text-base font-bold text-[#0f172a] truncate">{t.name}</p>
                    <span className="inline-block mt-1 bg-[#73c3ff]/15 text-[#002570] text-[11px] font-inter font-semibold px-2.5 py-0.5 rounded-full">{t.course}</span>
                    {t.course === "ACCA" ? (
                      <p className="mt-2 text-xs text-slate-600">{(t.scores || []).map((s) => `${s.paper}:${s.mark}`).join(" · ") || "—"}</p>
                    ) : (
                      <p className="mt-2 text-xs text-slate-600">{t.part} · <span className="font-semibold text-[#002570]">{t.mark}/{t.max_mark}</span></p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    {t.featured ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {t.featured ? "Public" : "Hidden"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(t)} data-testid={`admin-topper-edit-${t.id}`} className="w-9 h-9 rounded-full bg-[#002570]/10 text-[#002570] hover:bg-[#002570] hover:text-white flex items-center justify-center transition-colors" aria-label="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(t)} disabled={deletingId === t.id} data-testid={`admin-topper-delete-${t.id}`} className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center transition-colors disabled:opacity-60" aria-label="Delete">
                      {deletingId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-6 font-inter text-xs text-slate-400 text-center">Showing {filtered.length} of {list.length} toppers</p>
      </main>

      <TopperEditDialog
        open={open}
        onOpenChange={setOpen}
        topper={editing && editing.id ? editing : null}
        authHeader={authHeader}
        onSaved={onSaved}
      />
    </div>
  );
}
